import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Plus, Target, Compass, Users as UsersIcon, Send, Trash2, Search, X, Layers, Heart } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useSeoTitle } from "@/lib/useSeoTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth";

const emptyForm = {
  title: "", setting: "", tone: "", scenario: "", blurb: "",
  primary_goal: "", image: "",
};

// Map a world's `setting` string to a high-level genre tag used by the filter
// chips. Keep these strings lower-case for case-insensitive matching.
function genreFor(setting = "") {
  const s = setting.toLowerCase();
  if (s.includes("cyber") || s.includes("post-apoc") || s.includes("solarpunk") || s.includes("steampunk")) return "sci-fi";
  if (s.includes("cosmic horror") || s.includes("victorian occult")) return "horror";
  if (s.includes("wuxia") || s.includes("feudal japan")) return "eastern";
  if (s.includes("mythic hellas") || s.includes("egyptian")) return "mythic";
  if (s.includes("weird west")) return "frontier";
  if (s.includes("pirate") || s.includes("seafaring") || s.includes("caribbean")) return "pirate";
  if (s.includes("norse") || s.includes("northern sword")) return "norse";
  if (s.includes("dark ") || s.includes("infernal") || s.includes("gothic")) return "dark";
  return "fantasy";
}

const GENRE_CHIPS = [
  { id: "all",      label: "All",       hint: "Every realm" },
  { id: "fantasy",  label: "Fantasy",   hint: "Classic high & low fantasy" },
  { id: "dark",     label: "Dark",      hint: "Gothic, infernal, gritty" },
  { id: "sci-fi",   label: "Sci-fi",    hint: "Cyberpunk, post-apoc, steampunk" },
  { id: "horror",   label: "Horror",    hint: "Cosmic & gaslight occult" },
  { id: "eastern",  label: "Eastern",   hint: "Wuxia & feudal Japan" },
  { id: "mythic",   label: "Mythic",    hint: "Hellas, Aten, demigods" },
  { id: "frontier", label: "Frontier",  hint: "Weird west & beyond" },
  { id: "pirate",   label: "Pirate",    hint: "Seafaring sagas" },
  { id: "norse",    label: "Norse",     hint: "Ragnarok & frostfall" },
];

const TYPE_CHIPS = [
  { id: "all",      label: "All",                  hint: "Every world type" },
  { id: "goal",     label: "Goal-Driven Quests",   hint: "Curated sagas with a primary objective" },
  { id: "freeplay", label: "Free Play Sandboxes",  hint: "Open-ended realms for exploration" },
  { id: "community",label: "Community Realms",     hint: "Player-published community worlds" },
];

function WorldCard({ w, onLaunch, isMine = false, onDelete, canFavorite = false, favorited = false, onFavorite }) {
  return (
    <div
      data-testid={`world-card-${w.id}`}
      className="parchment gold-border rounded-xl overflow-hidden hover:candle-glow transition rise group flex flex-col"
    >
      {w.image && (
        <div className="relative h-44 overflow-hidden">
          <img
            src={w.image}
            alt={w.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#15121a] via-[#15121a]/30 to-transparent" />
          <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between gap-2">
            <div className="font-ui text-[10px] uppercase tracking-widest text-amber-300/90">
              {w.setting} · {w.tone}
            </div>
            {w.author_name && (
              <div className="font-ui text-[10px] uppercase tracking-widest text-amber-200/70 italic">
                by {w.author_name}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-2xl text-amber-50 mb-3">{w.title}</h3>
        <p className="font-body text-amber-100/75 leading-relaxed mb-4">{w.blurb}</p>
        {w.primary_goal && (
          <div className="mb-4 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-900/15">
            <div className="font-ui text-[10px] uppercase tracking-widest text-amber-300/80 mb-0.5 flex items-center gap-1">
              <Target className="w-3 h-3" /> Primary Goal
            </div>
            <div className="font-body text-sm text-amber-100">{w.primary_goal}</div>
          </div>
        )}
        <p className="font-body italic text-amber-200/55 text-sm border-l-2 border-amber-500/30 pl-3 mb-5">
          {w.scenario}
        </p>
        <div className="mt-auto flex gap-2">
          <Button
            data-testid={`world-launch-${w.id}`}
            className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui candle-glow flex-1"
            onClick={() => onLaunch(w)}
          >
            <Sparkles className="w-4 h-4 mr-2" /> Enter this realm
          </Button>
          {canFavorite && (
            <Button
              data-testid={`world-favorite-${w.id}`}
              variant="outline"
              className={favorited ? "border-rose-500/60 bg-rose-950/25 text-rose-200 hover:bg-rose-950/40" : "border-amber-500/30 text-amber-200 hover:bg-amber-900/20"}
              onClick={() => onFavorite?.(w)}
              title={favorited ? "Remove from favorites" : "Favorite this realm"}
            >
              <Heart className={favorited ? "w-4 h-4 fill-current" : "w-4 h-4"} />
            </Button>
          )}
          {isMine && onDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid={`world-delete-${w.id}`}
                  variant="outline"
                  className="border-red-700/50 text-red-300 hover:bg-red-900/20"
                  title="Unpublish your world"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="parchment gold-border">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display text-amber-100">
                    Unpublish &ldquo;{w.title}&rdquo;?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="font-body text-amber-200/70">
                    This removes the realm from the community library. It cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="font-ui">Keep it</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid={`world-delete-confirm-${w.id}`}
                    className="bg-red-700 hover:bg-red-600 text-amber-50 font-ui"
                    onClick={() => onDelete(w)}
                  >
                    Unpublish
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, label, blurb, count }) {
  return (
    <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 border-b border-amber-700/30 pb-4">
      <div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400" />
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-amber-50">
            {label}
          </h2>
        </div>
        <p className="font-body text-amber-100/60 text-sm sm:text-base italic mt-2 max-w-2xl">{blurb}</p>
      </div>
      <span className="font-display text-amber-300/80 text-sm sm:text-lg shrink-0 self-start sm:self-auto">
        {count} {count === 1 ? "realm" : "realms"}
      </span>
    </div>
  );
}

export default function Worlds() {
  const nav = useNavigate();
  const { user } = useAuth();
  useSeoTitle({
    title: "Story Worlds — TalesTalesTales AI Novel",
    description: "Browse 20+ curated illustrated story worlds and player-published community realms — high fantasy, cyberpunk, gothic horror, wuxia, weird west and more. Become the main character in your own AI novel.",
    path: "/worlds",
  });
  const [worlds, setWorlds] = useState([]);
  const [community, setCommunity] = useState([]);
  const [towers, setTowers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [publishing, setPublishing] = useState(false);
  const [genre, setGenre] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  useEffect(() => {
    api.get("/worlds/featured").then(r => setWorlds(r.data)).catch(() => {});
    api.get("/worlds/community").then(r => setCommunity(r.data)).catch(() => setCommunity([]));
    api.get("/towers").then(r => setTowers(r.data)).catch(() => setTowers([]));
    if (user) api.get("/worlds/community/favorites").then(r => setFavoriteIds(new Set(r.data.world_ids || []))).catch(() => {});
  }, []);

  const launch = (w) => {
    if (!user) { nav("/register"); return; }
    nav("/campaign/new", { state: { preset: w } });
  };

  const customWorld = () => {
    if (!user) { nav("/register"); return; }
    nav("/campaign/new");
  };

  // Fixed-progression tower launcher — SAO/Aincrad-style curated realms.
  // Seeds the campaign with tower_id + spawns hero on floor 1. Every replay
  // of the same tower shares the same floor layout and objectives — only
  // the narrative branches per-floor are freshly generated.
  const launchTower = (tower) => {
    if (!user) { nav("/register"); return; }
    nav("/campaign/new", {
      state: {
        preset: {
          title: tower.title,
          setting: tower.setting,
          tone: tower.tone,
          scenario: (
            "Fixed-progression tower — you begin on Floor 1 and must clear "
            + String(tower.floor_count) + " floors to escape. Each floor has "
            + "its own objective and boss; the layout is identical for every "
            + "player, but your choices within each floor shape the story."
          ),
          tower_id: tower.id,
        },
      },
    });
  };

  const submitPublish = async () => {
    if (!user) { nav("/register"); return; }
    if (!form.title || !form.setting || !form.tone || !form.scenario || !form.blurb) {
      toast.error("Please fill title, setting, tone, scenario and blurb.");
      return;
    }
    setPublishing(true);
    try {
      const cat = form.primary_goal.trim() ? "goal" : "freeplay";
      const r = await api.post("/worlds/publish", { ...form, category: cat });
      setCommunity((c) => [r.data, ...c]);
      toast.success("Your realm has been added to the community library.");
      setForm(emptyForm);
      setOpen(false);
    } catch {
      toast.error("Could not publish the realm.");
    } finally { setPublishing(false); }
  };

  const deleteCommunity = async (w) => {
    try {
      await api.delete(`/worlds/community/${w.id}`);
      setCommunity((c) => c.filter((x) => x.id !== w.id));
      toast("Realm unpublished.");
    } catch {
      toast.error("Could not unpublish.");
    }
  };

  const toggleFavorite = async (w) => {
    if (!user) { nav("/register"); return; }
    const already = favoriteIds.has(w.id);
    try {
      if (already) await api.delete(`/worlds/community/${w.id}/favorite`);
      else await api.post(`/worlds/community/${w.id}/favorite`);
      setFavoriteIds((current) => {
        const next = new Set(current);
        if (already) next.delete(w.id); else next.add(w.id);
        return next;
      });
      setCommunity((current) => current.map((world) => world.id === w.id
        ? { ...world, favorite_count: Math.max(0, (world.favorite_count || 0) + (already ? -1 : 1)) }
        : world));
    } catch {
      toast.error("Could not update this realm's favorite.");
    }
  };

  // Apply genre filter + free-text search across featured + community lists.
  const matchesFilters = (w) => {
    if (genre !== "all" && genreFor(w.setting) !== genre) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = [w.title, w.setting, w.tone, w.blurb, w.scenario, w.primary_goal]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  };

  const filteredFeatured = useMemo(() => worlds.filter(matchesFilters), [worlds, genre, search]);
  const filteredCommunity = useMemo(() => community.filter(matchesFilters), [community, genre, search]);

  const filteredGoalWorlds = useMemo(
    () => filteredFeatured.filter(w => (w.primary_goal || "").trim().length > 0),
    [filteredFeatured],
  );
  const filteredFreeplayWorlds = useMemo(
    () => filteredFeatured.filter(w => !(w.primary_goal || "").trim()),
    [filteredFeatured],
  );

  const typeCounts = {
    all: filteredFeatured.length + filteredCommunity.length,
    goal: filteredGoalWorlds.length,
    freeplay: filteredFreeplayWorlds.length,
    community: filteredCommunity.length,
  };

  // Counts per genre across the full featured list (for the chip badges)
  const genreCounts = useMemo(() => {
    const counts = { all: worlds.length };
    for (const w of worlds) {
      const g = genreFor(w.setting);
      counts[g] = (counts[g] || 0) + 1;
    }
    return counts;
  }, [worlds]);

  const goalWorlds = filteredGoalWorlds;
  const freeplayWorlds = filteredFreeplayWorlds;
  const isFiltering = typeFilter !== "all" || genre !== "all" || search.trim().length > 0;

  return (
    <div data-testid="worlds-page" className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <div id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* HEADER */}
        <div className="rise mb-10 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="font-ui text-xs tracking-[0.35em] text-amber-400/80 uppercase mb-3">Featured Realms</div>
            <div className="rounded-3xl border border-amber-700/30 bg-[#141018] p-4 mb-6 shadow-[0_15px_50px_-35px_rgba(255,187,0,0.45)]">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="font-display text-xl text-amber-50">Filter worlds by type</div>
                  <p className="font-body text-amber-200/70 text-sm mt-1">Choose a world category before you scroll — Goal-Driven Quests, Free Play Sandboxes, or Community Realms.</p>
                </div>
                {isFiltering && (
                  <button
                    data-testid="worlds-reset-filters-top"
                    type="button"
                    onClick={() => { setTypeFilter("all"); setGenre("all"); setSearch(""); }}
                    className="font-ui text-[10px] uppercase tracking-[0.35em] text-amber-300/80 hover:text-amber-100"
                  >
                    Reset filters
                  </button>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="worlds-type-top">
                {TYPE_CHIPS.map((t) => {
                  const active = typeFilter === t.id;
                  const count = typeCounts[t.id] ?? 0;
                  const disabled = t.id !== "all" && count === 0;
                  return (
                    <button
                      key={t.id}
                      data-testid={`worlds-type-top-${t.id}`}
                      type="button"
                      onClick={() => !disabled && setTypeFilter(t.id)}
                      disabled={disabled}
                      title={t.hint}
                      className={`px-3 py-1 rounded-full border font-ui text-[10px] uppercase tracking-widest transition flex items-center gap-2 ${
                        active
                          ? "bg-amber-600 text-stone-900 border-amber-500"
                          : disabled
                            ? "bg-transparent text-amber-200/30 border-amber-700/15 cursor-not-allowed"
                            : "bg-transparent text-amber-200/80 border-amber-700/30 hover:border-amber-500/60 hover:text-amber-100"
                      }`}
                    >
                      {t.label}
                      <span className={`font-display text-[10px] ${active ? "text-stone-900/70" : "text-amber-300/60"}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <h1 className="font-display text-5xl text-amber-50">Worlds awaiting a hero</h1>
            <p className="font-body text-amber-100/60 mt-3 max-w-2xl">
              Curated starting points for your saga — or forge a realm entirely your own and share it with the community.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="worlds-publish-btn"
                  size="lg"
                  variant="outline"
                  className="border-amber-500/50 text-amber-200 hover:bg-amber-900/20 font-ui h-12"
                >
                  <Send className="w-4 h-4 mr-2" /> Publish a realm
                </Button>
              </DialogTrigger>
              <DialogContent
                data-testid="publish-dialog"
                className="parchment gold-border max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl text-amber-100">
                    Publish a realm to the community
                  </DialogTitle>
                  <DialogDescription className="font-body text-amber-200/70 italic">
                    Other players will be able to launch sagas in your world. If you set a Primary Goal, it lands under Goal-Driven Quests; otherwise it&apos;s a Free Play Sandbox.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="font-ui text-[10px] uppercase tracking-widest text-amber-400/80">Title</label>
                      <Input
                        data-testid="publish-title"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        placeholder="The Hollow Crown of Veth"
                        maxLength={100}
                      />
                    </div>
                    <div>
                      <label className="font-ui text-[10px] uppercase tracking-widest text-amber-400/80">Setting</label>
                      <Input
                        data-testid="publish-setting"
                        value={form.setting}
                        onChange={(e) => setForm({ ...form, setting: e.target.value })}
                        placeholder="Dark High Fantasy"
                        maxLength={80}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-ui text-[10px] uppercase tracking-widest text-amber-400/80">Tone</label>
                    <Input
                      data-testid="publish-tone"
                      value={form.tone}
                      onChange={(e) => setForm({ ...form, tone: e.target.value })}
                      placeholder="Grim & Heroic"
                      maxLength={80}
                    />
                  </div>
                  <div>
                    <label className="font-ui text-[10px] uppercase tracking-widest text-amber-400/80">One-line blurb</label>
                    <Input
                      data-testid="publish-blurb"
                      value={form.blurb}
                      onChange={(e) => setForm({ ...form, blurb: e.target.value })}
                      placeholder="A queen long dead, three claimants, and a forest that whispers"
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <label className="font-ui text-[10px] uppercase tracking-widest text-amber-400/80">Scenario seed</label>
                    <Textarea
                      data-testid="publish-scenario"
                      rows={3}
                      value={form.scenario}
                      onChange={(e) => setForm({ ...form, scenario: e.target.value })}
                      placeholder="Set the opening hook: where the player begins, what's wrong, what calls them to act."
                      maxLength={600}
                    />
                  </div>
                  <div>
                    <label className="font-ui text-[10px] uppercase tracking-widest text-amber-400/80">
                      Primary goal (optional — adds to Goal-Driven Quests)
                    </label>
                    <Input
                      data-testid="publish-goal"
                      value={form.primary_goal}
                      onChange={(e) => setForm({ ...form, primary_goal: e.target.value })}
                      placeholder="Claim the Hollow Crown before the autumn moon."
                      maxLength={300}
                    />
                  </div>
                  <div>
                    <label className="font-ui text-[10px] uppercase tracking-widest text-amber-400/80">Cover image URL (optional)</label>
                    <Input
                      data-testid="publish-image"
                      value={form.image}
                      onChange={(e) => setForm({ ...form, image: e.target.value })}
                      placeholder="https://images.unsplash.com/..."
                      maxLength={600}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    data-testid="publish-submit"
                    disabled={publishing}
                    className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui"
                    onClick={submitPublish}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {publishing ? "Publishing..." : "Publish to community"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button
              data-testid="worlds-custom-btn"
              size="lg"
              className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-12 candle-glow"
              onClick={customWorld}
            >
              <Plus className="w-4 h-4 mr-2" /> Forge your own world
            </Button>
          </div>
        </div>

        {/* GENRE FILTER + SEARCH */}
        <div data-testid="worlds-filter-bar" className="mb-8 rise">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-amber-400/60 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                data-testid="worlds-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title, setting, tone, or scenario..."
                className="pl-9 pr-9 bg-[#15121a] border-amber-700/30 text-amber-100 font-body focus-visible:ring-amber-500"
                maxLength={80}
              />
              {search && (
                <button
                  data-testid="worlds-search-clear"
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-amber-300/60 hover:text-amber-100 transition"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {isFiltering && (
              <button
                data-testid="worlds-reset-filters"
                type="button"
                onClick={() => { setTypeFilter("all"); setGenre("all"); setSearch(""); }}
                className="font-ui text-xs uppercase tracking-widest text-amber-300/80 hover:text-amber-100 transition self-start md:self-auto"
              >
                Reset filters
              </button>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="worlds-genre-chips">
            {GENRE_CHIPS.map((g) => {
              const active = genre === g.id;
              const count = genreCounts[g.id] ?? 0;
              const disabled = g.id !== "all" && count === 0;
              return (
                <button
                  key={g.id}
                  data-testid={`worlds-genre-${g.id}`}
                  type="button"
                  onClick={() => !disabled && setGenre(g.id)}
                  disabled={disabled}
                  title={g.hint}
                  className={`px-3 py-1.5 rounded-full border font-ui text-xs uppercase tracking-widest transition flex items-center gap-1.5 ${
                    active
                      ? "bg-amber-600 text-stone-900 border-amber-500 candle-glow"
                      : disabled
                        ? "bg-transparent text-amber-200/25 border-amber-700/15 cursor-not-allowed"
                        : "bg-transparent text-amber-200/80 border-amber-700/30 hover:border-amber-500/60 hover:text-amber-100"
                  }`}
                >
                  {g.label}
                  <span className={`font-display text-[10px] ${active ? "text-stone-900/70" : "text-amber-300/60"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          {isFiltering && filteredFeatured.length === 0 && filteredCommunity.length === 0 && (
            <p
              data-testid="worlds-empty-state"
              className="mt-6 font-body italic text-amber-200/70 text-center border border-amber-700/30 rounded-lg py-6"
            >
              No realms match these filters. Try resetting or forging your own.
            </p>
          )}
        </div>

        {/* FIXED-PROGRESSION TOWERS — SAO/Aincrad-style curated realms
            where every player follows the same floor blueprint but the
            story branches inside each floor. Sits above Goal-Driven Quests
            because these are the most structured / gamified offerings. */}
        {towers.length > 0 && (
          <section
            className="mb-12"
            data-testid="worlds-section-towers"
          >
            <SectionHeader
              icon={Layers}
              label="Fixed-Progression Towers"
              blurb="Climb curated floor-by-floor realms — the world is identical every replay, but your story is not. Aincrad-style."
              count={towers.length}
            />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {towers.map((t) => (
                <div
                  key={t.id}
                  data-testid={`tower-card-${t.id}`}
                  className="parchment gold-border rounded-xl overflow-hidden flex flex-col"
                >
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="w-4 h-4 text-amber-400" />
                      <span
                        data-testid={`tower-floor-count-${t.id}`}
                        className="font-ui text-[10px] uppercase tracking-widest text-amber-300/80"
                      >
                        {t.floor_count} floors · {t.genre}
                      </span>
                    </div>
                    <h3 className="font-display text-xl text-amber-50 mb-1">{t.title}</h3>
                    <p className="font-body text-sm text-amber-200/70 italic mb-3">{t.tone}</p>
                    <p className="font-body text-sm text-amber-100/70 leading-relaxed mb-4 flex-1">
                      {t.blurb}
                    </p>
                    <Button
                      data-testid={`tower-launch-${t.id}`}
                      onClick={() => launchTower(t)}
                      className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui w-full"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Enter Floor 1
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* GOAL-DRIVEN QUESTS */}
        {(typeFilter === "all" || typeFilter === "goal") && goalWorlds.length > 0 && (
          <section className="mb-12" data-testid="worlds-section-goal">
            <SectionHeader
              icon={Target}
              label="Goal-Driven Quests"
              blurb="Curated sagas with a clear objective and stakes that escalate as you play."
              count={goalWorlds.length}
            />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {goalWorlds.map((w) => (
                <WorldCard key={w.id} w={w} onLaunch={launch} />
              ))}
            </div>
          </section>
        )}

        {/* FREE PLAY SANDBOXES */}
        {(typeFilter === "all" || typeFilter === "freeplay") && (
          <section className="mb-12" data-testid="worlds-section-freeplay">
          <SectionHeader
            icon={Compass}
            label="Free Play Sandboxes"
            blurb="Open-ended realms — wander, scheme, build, or burn it all down."
            count={freeplayWorlds.length + 1 /* custom tile */}
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Custom-world tile leading the grid */}
            <button
              data-testid="worlds-custom-tile"
              onClick={customWorld}
              className="parchment gold-border rounded-xl p-8 flex flex-col items-center justify-center text-center min-h-[360px] hover:candle-glow transition cursor-pointer group"
            >
              <div className="w-16 h-16 rounded-full border-2 border-amber-500/50 flex items-center justify-center mb-4 group-hover:border-amber-400 transition">
                <Plus className="w-8 h-8 text-amber-400 group-hover:scale-110 transition" />
              </div>
              <div className="font-ui text-[10px] uppercase tracking-widest text-amber-400/70">Blank Scroll</div>
              <h3 className="font-display text-2xl text-amber-50 mt-2 mb-3">Forge your own world</h3>
              <p className="font-body text-amber-100/70 leading-relaxed">
                Name a setting, set a tone, plant a scenario seed — the DM will conjure the rest.
              </p>
              <span className="mt-5 font-ui text-sm text-amber-300 group-hover:text-amber-200">
                Begin a blank saga →
              </span>
            </button>
            {freeplayWorlds.map((w) => (
              <WorldCard key={w.id} w={w} onLaunch={launch} />
            ))}
          </div>
        </section>
        )}

        {(typeFilter === "all" || typeFilter === "community") && (
          <section data-testid="worlds-section-community">
          <SectionHeader
            icon={UsersIcon}
            label="Community Realms"
            blurb="Player-published worlds. Forge your own and add it to the library above."
            count={community.length}
          />
          {community.length === 0 ? (
            <div className="parchment gold-border rounded-xl p-10 text-center">
              <p className="font-body text-amber-100/70 italic">
                The community library is still unwritten.
              </p>
              <Button
                data-testid="worlds-publish-first"
                className="mt-4 bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui"
                onClick={() => setOpen(true)}
              >
                <Send className="w-4 h-4 mr-2" /> Be the first to publish
              </Button>
            </div>
          ) : filteredCommunity.length === 0 ? (
            <div className="parchment gold-border rounded-xl p-10 text-center">
              <p className="font-body text-amber-100/70 italic">
                No community realms match your filters.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCommunity.map((w) => (
                <WorldCard
                  key={w.id}
                  w={w}
                  onLaunch={launch}
                  isMine={user && w.author_id === user.id}
                  onDelete={deleteCommunity}
                  canFavorite
                  favorited={favoriteIds.has(w.id)}
                  onFavorite={toggleFavorite}
                />
              ))}
            </div>
          )}
        </section>        )}      </div>
      <Footer />
    </div>
  );
}
